"use client";

/**
 * Figures for blog posts, addressed from markdown by name.
 *
 * A post writes a fenced block:
 *
 *     ```chart
 *     breakeven-grid
 *     ```
 *
 * and the post page's `pre` override swaps it for the matching figure
 * (owner, 2026-09-21: "are there anything we could build charts,
 * visuals for the buy vs rent blog post" -> "whatever is proper", then
 * "they are interactive, right, so we could add some interactivity").
 *
 * Rules this file follows:
 *  - The reader moves the assumptions that matter, the price, the rate,
 *    the down payment, and the answer recomputes in the browser through
 *    lib/rentbuy-math, the same module the rent-versus-buy calculator
 *    uses, so a figure and the calculator can never disagree.
 *  - Measurements stay fixed: yields by room count and by district are
 *    crawl results, not assumptions, and nothing about them is the
 *    reader's to change.
 *  - No chart library. Divs and one inline SVG.
 *  - No hardcoded colours: every surface and stroke is a theme token,
 *    so the figures follow dark mode.
 *  - Markers are HTML, never SVG circles: the plot uses a stretched
 *    viewBox, which turns a circle into an ellipse.
 *  - Semantic scale per DESIGN.md: emerald good, amber careful, rose no.
 */
import { Fragment, useMemo, useState } from "react";
import type { Locale } from "@/lib/i18n-config";
import { annuity, rentVsBuy } from "@/lib/rentbuy-math";
import { SEGMENT_ON, rangeFill } from "@/lib/ui-classes";

type Dict = Record<Locale, string>;
const t = (d: Dict, locale: Locale) => d[locale] ?? d.az;

/* The flat the whole article works through. */
const RENT = 900;
const DOWN_PCT = 20;
const TERM = 20;
const PRICES = [150_000, 250_000, 400_000] as const;
/** Preferential, ordinary state, and a bank's own mortgage. The bank
 *  figure is the effective rate banks actually publish for an internal
 *  mortgage in 2026 (about 12% to 16.5%), not an advertised entry rate. */
const RATES = [4, 8, 14] as const;

const SOURCE: Dict = {
  az: "Mənbə: bina.az elanları, sentyabr 2026 (249 474 satılıq, 130 815 aylıq kirayə).",
  ru: "Источник: объявления bina.az, сентябрь 2026 (249 474 о продаже, 130 815 об аренде).",
  en: "Source: bina.az listings, September 2026 (249,474 for sale, 130,815 monthly rentals).",
};
const YEARS_SHORT: Dict = { az: "il", ru: "лет", en: "yrs" };
const RATE_LABEL: Dict[] = [
  { az: "4% güzəştli", ru: "4% льготная", en: "4% preferential" },
  { az: "8% adi", ru: "8% обычная", en: "8% ordinary" },
  { az: "14% bank", ru: "14% банк", en: "14% bank" },
];
const PRICE_LABEL: Dict = { az: "Mənzilin qiyməti", ru: "Цена квартиры", en: "Flat price" };
const RATE_PICK: Dict = { az: "İpoteka faizi", ru: "Ставка ипотеки", en: "Mortgage rate" };
const NEVER: Dict = { az: "30+", ru: "30+", en: "30+" };
const TRY_IT: Dict = {
  az: "Rəqəmləri dəyişin, cavab dərhal yenilənir.",
  ru: "Меняйте параметры, ответ пересчитывается сразу.",
  en: "Change the assumptions and the answer updates as you go.",
};

function money(n: number, locale: Locale): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, locale === "en" ? "," : " ");
}
function dec(n: number, digits: number, locale: Locale): string {
  return n.toFixed(digits).replace(".", locale === "en" ? "." : ",");
}
function beYears(price: number, rent: number, ratePct: number, downPct = DOWN_PCT, growthPct = 0) {
  return rentVsBuy({
    price,
    rent,
    downAmount: (price * downPct) / 100,
    ratePct,
    years: TERM,
    growthPct,
    horizonYears: 30,
  }).breakEven;
}

/* ── shared bits ─────────────────────────────────────────────────── */

function Figure({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <figure className="my-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
      <figcaption className="mb-3 text-sm font-bold text-gray-900">{title}</figcaption>
      {children}
      <p className="mt-3 border-t border-gray-100 pt-2 text-[11px] leading-snug text-gray-400">
        {note}
      </p>
    </figure>
  );
}

function Chips<T extends number>({
  label,
  options,
  value,
  onChange,
  render,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  render: (v: T, i: number) => string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 text-[11px] font-semibold text-gray-500">{label}</span>
      {options.map((o, i) => (
        <button
          key={o}
          type="button"
          aria-pressed={value === o}
          onClick={() => onChange(o)}
          className={`rounded-md px-2 py-1 text-[11px] font-semibold tabular-nums transition-colors ${
            value === o
              ? SEGMENT_ON
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10"
          }`}
        >
          {render(o, i)}
        </button>
      ))}
    </div>
  );
}

/** emerald soon, amber within a normal stay, rose too far out. */
function tone(years: number | null): string {
  if (years == null) return "bg-rose-50 text-rose-700";
  if (years <= 5) return "bg-emerald-50 text-emerald-700";
  if (years <= 15) return "bg-amber-50 text-amber-800";
  return "bg-rose-50 text-rose-700";
}

/* ── 1. break-even grid: rent x rate, at a price you pick ────────── */

const RENTS = [700, 900, 1100, 1300, 1500];

function BreakevenGrid({ locale }: { locale: Locale }) {
  const [price, setPrice] = useState<number>(250_000);
  const rows = useMemo(
    () => RENTS.map((rent) => ({ rent, years: RATES.map((r) => beYears(price, rent, r)) })),
    [price],
  );
  const title: Dict = {
    az: "Almaq neçənci ildən kirayədən ucuz olur",
    ru: "С какого года покупка становится дешевле аренды",
    en: "The year buying becomes cheaper than renting",
  };
  const rentHead: Dict = { az: "Aylıq kirayə", ru: "Аренда в месяц", en: "Monthly rent" };
  const note: Dict = {
    az: `${DOWN_PCT}% ilkin ödəniş, ${TERM} illik kredit, qiymət artımı 0%.`,
    ru: `Взнос ${DOWN_PCT}%, кредит на ${TERM} лет, рост цены 0%.`,
    en: `${DOWN_PCT}% down, a ${TERM}-year loan, price growth 0%.`,
  };
  return (
    <Figure title={t(title, locale)} note={`${t(note, locale)} ${t(TRY_IT, locale)}`}>
      <Chips
        label={t(PRICE_LABEL, locale)}
        options={PRICES}
        value={price}
        onChange={setPrice}
        render={(p) => `${money(p / 1000, locale)}k AZN`}
      />
      <div className="mt-3 grid grid-cols-4 gap-1 text-center text-sm">
        <div className="px-2 py-1.5 text-left text-[11px] font-semibold text-gray-500">
          {t(rentHead, locale)}
        </div>
        {RATE_LABEL.map((c) => (
          <div key={c.en} className="px-1 py-1.5 text-[11px] font-semibold text-gray-500">
            {t(c, locale)}
          </div>
        ))}
        {rows.map((row) => (
          <Fragment key={row.rent}>
            <div className="flex items-center px-2 py-2 text-left text-[13px] font-semibold tabular-nums text-gray-700">
              {money(row.rent, locale)} AZN
            </div>
            {row.years.map((y, i) => (
              <div key={i} className={`rounded-lg px-1 py-2 font-bold tabular-nums ${tone(y)}`}>
                {y == null ? t(NEVER, locale) : y}
                <span className="ml-1 text-[10px] font-semibold opacity-70">
                  {t(YEARS_SHORT, locale)}
                </span>
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </Figure>
  );
}

/* ── 2. what a small growth assumption does ──────────────────────── */

const GROWTHS = [0, 1, 2, 3, 5];

function GrowthSensitivity({ locale }: { locale: Locale }) {
  const [rate, setRate] = useState<number>(8);
  const rows = useMemo(
    () => GROWTHS.map((g) => ({ growth: g, years: beYears(250_000, RENT, rate, DOWN_PCT, g) })),
    [rate],
  );
  const max = Math.max(...rows.map((r) => r.years ?? 30));
  const title: Dict = {
    az: "Qiymət artımı fərziyyəsi cavabı necə dəyişir",
    ru: "Как предположение о росте цены меняет ответ",
    en: "How the price-growth assumption moves the answer",
  };
  const left: Dict = {
    az: "İllik qiymət artımı",
    ru: "Годовой рост цены",
    en: "Annual price growth",
  };
  const right: Dict = {
    az: "Almaq neçənci ildən ucuzdur",
    ru: "С какого года покупка дешевле",
    en: "Buying is cheaper from year",
  };
  const note: Dict = {
    az: `250 000 AZN-lik mənzil, ${DOWN_PCT}% ilkin ödəniş, ${TERM} illik kredit, kirayə ayda ${RENT} AZN.`,
    ru: `Квартира за 250 000 AZN, взнос ${DOWN_PCT}%, кредит на ${TERM} лет, аренда ${RENT} AZN в месяц.`,
    en: `A 250,000 AZN flat, ${DOWN_PCT}% down, a ${TERM}-year loan, rent of ${RENT} AZN a month.`,
  };
  return (
    <Figure title={t(title, locale)} note={`${t(note, locale)} ${t(TRY_IT, locale)}`}>
      <Chips
        label={t(RATE_PICK, locale)}
        options={RATES}
        value={rate}
        onChange={setRate}
        render={(_, i) => t(RATE_LABEL[i], locale)}
      />
      <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-gray-500">
        <span>{t(left, locale)}</span>
        <span>{t(right, locale)}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {rows.map((g) => (
          <div key={g.growth} className="flex items-center gap-2">
            <span className="w-10 shrink-0 text-right text-[13px] font-semibold tabular-nums text-gray-700">
              {g.growth}%
            </span>
            <span className="h-5 flex-1 overflow-hidden rounded bg-gray-100">
              <span
                className={`block h-full rounded transition-[width] duration-200 ease-out ${
                  g.years == null
                    ? "bg-rose-400"
                    : g.years <= 5
                      ? "bg-emerald-500"
                      : g.years <= 15
                        ? "bg-amber-500"
                        : "bg-rose-400"
                }`}
                style={{ width: `${Math.max(4, ((g.years ?? 30) / max) * 100)}%` }}
              />
            </span>
            <span className="w-16 shrink-0 text-right text-[13px] font-bold tabular-nums text-gray-900">
              {g.years == null ? t(NEVER, locale) : g.years}{" "}
              <span className="text-[10px] font-semibold text-gray-400">
                {t(YEARS_SHORT, locale)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </Figure>
  );
}

/* ── 3. the running totals that cross ────────────────────────────── */

function Crossover({ locale }: { locale: Locale }) {
  const [rate, setRate] = useState<number>(8);
  const [hover, setHover] = useState<number | null>(null);
  const span = 25;
  const res = useMemo(
    () =>
      rentVsBuy({
        price: 250_000,
        rent: RENT,
        downAmount: (250_000 * DOWN_PCT) / 100,
        ratePct: rate,
        years: TERM,
        horizonYears: span,
      }),
    [rate],
  );
  const title: Dict = {
    az: "Yığılmış xərc: almaq və kirayə",
    ru: "Накопленные расходы: покупка и аренда",
    en: "Running cost: buying against renting",
  };
  const ownL: Dict = { az: "Almaq", ru: "Покупка", en: "Buying" };
  const rentL: Dict = { az: "Kirayə", ru: "Аренда", en: "Renting" };
  const crossL: Dict = {
    az: "{n}-ci ildə bərabərləşir",
    ru: "сравниваются на {n}-м году",
    en: "they meet in year {n}",
  };
  const noCross: Dict = {
    az: "25 il ərzində bərabərləşmir",
    ru: "не сравниваются за 25 лет",
    en: "they do not meet within 25 years",
  };
  const note: Dict = {
    az: `250 000 AZN-lik mənzil, ${DOWN_PCT}% ilkin ödəniş, ${TERM} illik kredit, kirayə ${RENT} AZN, qiymət artımı 0%.`,
    ru: `Квартира за 250 000 AZN, взнос ${DOWN_PCT}%, кредит на ${TERM} лет, аренда ${RENT} AZN, рост цены 0%.`,
    en: `A 250,000 AZN flat, ${DOWN_PCT}% down, a ${TERM}-year loan, rent ${RENT} AZN, price growth 0%.`,
  };
  const W = 300;
  const H = 110;
  const n = res.rows.length;
  const maxV = Math.max(...res.rows.flatMap((r) => [r.own, r.rent]));
  const step = 100_000;
  const axisMax = Math.max(step, Math.ceil(maxV / step) * step);
  const px = (i: number) => (i / (n - 1)) * W;
  const py = (v: number) => H - (v / axisMax) * H;
  const path = (key: "own" | "rent") =>
    res.rows.map((r, i) => `${i ? "L" : "M"}${px(i).toFixed(1)},${py(r[key]).toFixed(1)}`).join(" ");
  const ticks = Array.from({ length: axisMax / step + 1 }, (_, k) => k * step);
  const be = res.breakEven != null && res.breakEven <= n ? res.breakEven : null;
  const at = hover != null ? res.rows[hover - 1] : null;
  const pick = (clientX: number, el: HTMLElement) => {
    const box = el.getBoundingClientRect();
    if (box.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    setHover(Math.min(n, Math.max(1, Math.round(ratio * (n - 1)) + 1)));
  };
  return (
    <Figure title={t(title, locale)} note={`${t(note, locale)} ${t(TRY_IT, locale)}`}>
      <Chips
        label={t(RATE_PICK, locale)}
        options={RATES}
        value={rate}
        onChange={setRate}
        render={(_, i) => t(RATE_LABEL[i], locale)}
      />
      <div className="mt-3 flex items-center gap-4 text-[11px] font-semibold">
        <span className="flex items-center gap-1.5 text-indigo-700">
          <i className="inline-block h-2 w-2 rounded-sm bg-indigo-600" />
          {t(ownL, locale)}
        </span>
        <span className="flex items-center gap-1.5 text-amber-600">
          <i className="inline-block h-2 w-2 rounded-sm bg-amber-500" />
          {t(rentL, locale)}
        </span>
      </div>
      <div className="mt-2 flex gap-2">
        <div className="flex h-32 w-12 shrink-0 flex-col justify-between py-px text-right text-[10px] leading-none tabular-nums text-gray-400">
          {[...ticks].reverse().map((v) => (
            <span key={v}>{v === 0 ? "0" : `${v / 1000}k`}</span>
          ))}
        </div>
        <div
          className="relative h-32 flex-1 touch-pan-y"
          onPointerMove={(e) => pick(e.clientX, e.currentTarget)}
          onPointerDown={(e) => pick(e.clientX, e.currentTarget)}
          onPointerLeave={() => setHover(null)}
        >
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full">
            {ticks.map((v) => (
              <line
                key={v}
                x1={0}
                x2={W}
                y1={py(v)}
                y2={py(v)}
                className="stroke-gray-200"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {be != null && (
              <line
                x1={px(be - 1)}
                x2={px(be - 1)}
                y1={0}
                y2={H}
                className="stroke-gray-400"
                strokeWidth={1}
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {hover != null && (
              <line
                x1={px(hover - 1)}
                x2={px(hover - 1)}
                y1={0}
                y2={H}
                className="stroke-gray-400"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            )}
            <path
              d={path("rent")}
              fill="none"
              className="stroke-amber-500"
              strokeWidth={2}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={path("own")}
              fill="none"
              className="stroke-indigo-600"
              strokeWidth={2}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {/* Markers in HTML: a circle inside a stretched viewBox comes
              out as an ellipse (owner, 2026-09-21: "the point of
              intersection is squeezed, it's not a perfect circle"). */}
          {be != null && hover == null && (
            <span
              className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-900 ring-2 ring-[var(--color-paper)]"
              style={{
                left: `${((be - 1) / (n - 1)) * 100}%`,
                top: `${(py(res.rows[be - 1].own) / H) * 100}%`,
              }}
            />
          )}
          {at && hover != null && (
            <>
              <span
                className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500 ring-2 ring-[var(--color-paper)]"
                style={{
                  left: `${((hover - 1) / (n - 1)) * 100}%`,
                  top: `${(py(at.rent) / H) * 100}%`,
                }}
              />
              <span
                className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-600 ring-2 ring-[var(--color-paper)]"
                style={{
                  left: `${((hover - 1) / (n - 1)) * 100}%`,
                  top: `${(py(at.own) / H) * 100}%`,
                }}
              />
              <div
                className="pointer-events-none absolute top-0 z-10 w-max -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] leading-tight shadow-sm"
                style={{ left: `${Math.min(84, Math.max(16, ((hover - 1) / (n - 1)) * 100))}%` }}
              >
                <div className="font-bold text-gray-900">
                  {hover} {t(YEARS_SHORT, locale)}
                </div>
                <div className="mt-0.5 tabular-nums text-indigo-700">
                  {t(ownL, locale)} {money(at.own, locale)} AZN
                </div>
                <div className="tabular-nums text-amber-600">
                  {t(rentL, locale)} {money(at.rent, locale)} AZN
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="relative ml-14 mt-1 h-3 text-[10px] tabular-nums text-gray-400">
        <span className="absolute left-0">1</span>
        {be != null && be > 1 && be < n && (
          <span
            className="absolute -translate-x-1/2 font-bold text-gray-600"
            style={{ left: `${((be - 1) / (n - 1)) * 100}%` }}
          >
            {be}
          </span>
        )}
        <span className="absolute right-0">
          {n} {t(YEARS_SHORT, locale)}
        </span>
      </div>
      <p className="ml-14 mt-1.5 text-[11px] font-semibold text-gray-600">
        {be != null ? t(crossL, locale).replace("{n}", String(be)) : t(noCross, locale)}
      </p>
    </Figure>
  );
}

/* ── 4. yield by room count (measured, not adjustable) ───────────── */

const ROOMS = [
  { rooms: 1, yieldPct: 5.81, years: 17.2 },
  { rooms: 2, yieldPct: 5.08, years: 19.7 },
  { rooms: 3, yieldPct: 4.73, years: 21.2 },
  { rooms: 4, yieldPct: 4.43, years: 22.6 },
];

function YieldRooms({ locale }: { locale: Locale }) {
  const title: Dict = {
    az: "İllik kirayə gəliri, otaq sayına görə",
    ru: "Годовая доходность аренды по числу комнат",
    en: "Annual rental yield by room count",
  };
  const room: Dict = { az: "otaq", ru: "комн.", en: "rooms" };
  const payback: Dict = {
    az: "Sağdakı ikinci rəqəm: kirayə ilə qiyməti çıxarmaq neçə il çəkir.",
    ru: "Второе число справа: за сколько лет аренда окупает цену.",
    en: "The second figure: years of rent needed to equal the price.",
  };
  const max = ROOMS[0].yieldPct;
  return (
    <Figure title={t(title, locale)} note={`${t(payback, locale)} ${t(SOURCE, locale)}`}>
      <div className="space-y-2">
        {ROOMS.map((r) => (
          <div key={r.rooms} className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-[12px] font-semibold tabular-nums text-gray-700">
              {r.rooms} {t(room, locale)}
            </span>
            <span className="h-6 flex-1 overflow-hidden rounded bg-gray-100">
              <span
                className="block h-full rounded bg-indigo-500"
                style={{ width: `${(r.yieldPct / max) * 100}%` }}
              />
            </span>
            <span className="w-28 shrink-0 text-right text-[12px] tabular-nums">
              <b className="text-gray-900">{dec(r.yieldPct, 2, locale)}%</b>
              <span className="ml-1 text-gray-400">
                {dec(r.years, 1, locale)} {t(YEARS_SHORT, locale)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </Figure>
  );
}

/* ── 5. the district barely matters ──────────────────────────────── */

const DISTRICTS = [
  { name: "28 May", yieldPct: 4.68 },
  { name: "20 Yanvar", yieldPct: 4.62 },
  { name: "Ş. İ. Xətai", yieldPct: 4.62 },
  { name: "8 Noyabr", yieldPct: 4.59 },
  { name: "Nəsimi", yieldPct: 4.55 },
  { name: "Nizami", yieldPct: 4.49 },
  { name: "İnşaatçılar", yieldPct: 4.43 },
  { name: "Memar Əcəmi", yieldPct: 4.38 },
  { name: "Yasamal", yieldPct: 4.37 },
];

function YieldDistricts({ locale }: { locale: Locale }) {
  const title: Dict = {
    az: "Mərkəzi ərazilərdə gəlirlilik demək olar ki, eynidir",
    ru: "В центральных районах доходность почти не отличается",
    en: "The yield barely moves across central districts",
  };
  const note: Dict = {
    az: "Bütün ərazilər 4,37% ilə 4,68% arasındadır: rayon seçimi nisbəti dəyişmir.",
    ru: "Все районы укладываются между 4,37% и 4,68%: выбор района не меняет соотношение.",
    en: "Every district lands between 4.37% and 4.68%, so the choice of district does not change the ratio.",
  };
  const lo = 4.0;
  const hi = 5.0;
  return (
    <Figure title={t(title, locale)} note={`${t(note, locale)} ${t(SOURCE, locale)}`}>
      <div className="space-y-1">
        {DISTRICTS.map((d) => (
          <div key={d.name} className="flex items-center gap-2">
            <span className="w-24 shrink-0 truncate text-[12px] font-semibold text-gray-700">
              {d.name}
            </span>
            <span className="relative h-4 flex-1 rounded bg-gray-100">
              <span
                className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-600"
                style={{ left: `${((d.yieldPct - lo) / (hi - lo)) * 100}%` }}
              />
            </span>
            <span className="w-12 shrink-0 text-right text-[12px] font-bold tabular-nums text-gray-900">
              {dec(d.yieldPct, 2, locale)}%
            </span>
          </div>
        ))}
      </div>
    </Figure>
  );
}

/* ── 6. does the tenant pay your mortgage ────────────────────────── */

function TenantCoverage({ locale }: { locale: Locale }) {
  const [rate, setRate] = useState<number>(8);
  const [downPct, setDownPct] = useState(20);
  const price = 250_000;
  const payment = annuity(price - (price * downPct) / 100, rate, TERM);
  const covered = payment > 0 ? (RENT / payment) * 100 : 0;
  const gap = Math.round(payment - RENT);
  const title: Dict = {
    az: "Kirayəçi ipotekanızın neçə faizini ödəyir",
    ru: "Какую часть ипотеки покрывает арендатор",
    en: "How much of your mortgage the tenant covers",
  };
  const downL: Dict = { az: "İlkin ödəniş", ru: "Первоначальный взнос", en: "Down payment" };
  const paymentL: Dict = { az: "Aylıq ödəniş", ru: "Платёж в месяц", en: "Monthly payment" };
  const coveredL: Dict = { az: "Kirayə ödəyir", ru: "Платит аренда", en: "Rent pays" };
  const gapL: Dict = { az: "Cibinizdən çıxır", ru: "Из вашего кармана", en: "Out of your pocket" };
  const note: Dict = {
    az: `250 000 AZN-lik mənzil, ${TERM} illik kredit, aylıq kirayə ${RENT} AZN (bazar ortalaması).`,
    ru: `Квартира за 250 000 AZN, кредит на ${TERM} лет, аренда ${RENT} AZN в месяц (среднее по рынку).`,
    en: `A 250,000 AZN flat, a ${TERM}-year loan, rent of ${RENT} AZN a month (the market average).`,
  };
  return (
    <Figure title={t(title, locale)} note={`${t(note, locale)} ${t(TRY_IT, locale)}`}>
      <Chips
        label={t(RATE_PICK, locale)}
        options={RATES}
        value={rate}
        onChange={setRate}
        render={(_, i) => t(RATE_LABEL[i], locale)}
      />
      <label className="mt-3 block">
        <span className="flex items-baseline justify-between text-[11px] font-semibold text-gray-500">
          {t(downL, locale)}
          <b className="text-[13px] tabular-nums text-gray-900">{downPct}%</b>
        </span>
        <input
          type="range"
          min={10}
          max={80}
          step={5}
          value={downPct}
          onChange={(e) => setDownPct(Number(e.target.value))}
          aria-label={t(downL, locale)}
          className="mt-1 w-full"
          style={rangeFill(downPct, 10, 80)}
        />
      </label>
      <div className="mt-3 h-7 overflow-hidden rounded-lg bg-gray-100">
        <div
          className={`flex h-full items-center justify-end px-2 text-[11px] font-bold text-white transition-[width] duration-200 ease-out ${
            covered >= 100 ? "bg-emerald-500" : covered >= 75 ? "bg-amber-500" : "bg-rose-400"
          }`}
          style={{ width: `${Math.min(100, Math.max(6, covered))}%` }}
        >
          {Math.round(covered)}%
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-gray-100 px-2 py-2">
          <dt className="text-[10px] font-semibold text-gray-500">{t(paymentL, locale)}</dt>
          <dd className="text-sm font-bold tabular-nums text-gray-900">
            {money(payment, locale)} AZN
          </dd>
        </div>
        <div className="rounded-lg bg-gray-100 px-2 py-2">
          <dt className="text-[10px] font-semibold text-gray-500">{t(coveredL, locale)}</dt>
          <dd className="text-sm font-bold tabular-nums text-gray-900">{money(RENT, locale)} AZN</dd>
        </div>
        <div className="rounded-lg bg-gray-100 px-2 py-2">
          <dt className="text-[10px] font-semibold text-gray-500">{t(gapL, locale)}</dt>
          <dd
            className={`text-sm font-bold tabular-nums ${gap > 0 ? "text-rose-600" : "text-emerald-600"}`}
          >
            {gap > 0 ? money(gap, locale) : 0} AZN
          </dd>
        </div>
      </dl>
    </Figure>
  );
}

/* ── registry ────────────────────────────────────────────────────── */

const CHARTS: Record<string, (p: { locale: Locale }) => React.ReactElement> = {
  "breakeven-grid": BreakevenGrid,
  "growth-sensitivity": GrowthSensitivity,
  crossover: Crossover,
  "yield-rooms": YieldRooms,
  "yield-districts": YieldDistricts,
  "tenant-coverage": TenantCoverage,
};

/** Unknown ids render nothing rather than breaking the post. */
export default function BlogChart({ id, locale }: { id: string; locale: Locale }) {
  const Chart = CHARTS[id.trim()];
  return Chart ? <Chart locale={locale} /> : null;
}
